import React, { useEffect, useState } from 'react';
import {
  FinanceResponse,
  FinanceApplication,
  UpstartApplicationStatus,
} from '@upstart/harmony/lib/legos/financingTypes';
import { Car } from '@upstart/harmony/lib/legos/carTypes';
import { FinanceProvider } from '@upstart/harmony/lib/legos/constants';
import { Deal } from '@upstart/harmony/lib/legos/dealTypes';
import { getImageForFinanceSource } from '@upstart/harmony/lib/metadata';
import { capitalize, isNumber } from 'lodash';
import moment from 'moment';
import { FinanceResponseStatus } from '@upstart/harmony/lib/constants';
import { useAsyncCallback } from 'react-async-hook';
// @ts-ignore
import Img from 'react-image';
import { fmtPercent, fmtPrice } from '@upstart/harmony/lib/formatter';
import { Pencil } from '@upstart/harmony/lib/legos/pencilTypes';
import { DealerWithFlags } from '@upstart/harmony/lib/legos/dealerTypes';
import { getFinanceResponseTotalAPR } from '@upstart/harmony/lib/payment-utils';

import DataTable from '../../components/DataTable';
import { PercentageInput } from '../../components/PencilEditor/Input';
import PaymentsService from '../../services/PaymentsService';
import RenderUpstartOffer from './RenderUpstartOffer';

interface FinanceResponseWithOverrides extends FinanceResponse {
  selected?: boolean;
  overrides?: {
    markup?: number;
    monthly?: number;
  };
}

interface Props {
  response: FinanceResponse | null;
  application: FinanceApplication | null;
  applicationStatus: UpstartApplicationStatus | null;
  pencilResponse: FinanceResponseWithOverrides | null;
  deal: Deal;
  car: Car;
  setResponse: Function;
  reloadResponses: VoidFunction;
  showRicTab: boolean;
  setShowRicTab: (isShown: boolean) => void;
  pencil: Pencil;
  dealer: DealerWithFlags;
}

const getDealRecap = (
  application: FinanceApplication | null,
  response: FinanceResponse | null,
  car: Car,
) => {
  const applicationData = application?.data || {
    financeType: '',
    financeAmount: '',
    apr: 0,
    months: '',
    monthly: '',
    downPayment: 0,
    rebate: 0,
  };
  const responseData = response?.data || {};
  const recap = [];
  if (responseData.financeType) {
    recap.push([
      'Transaction Type',
      capitalize(responseData.financeType),
      capitalize(applicationData.financeType),
    ]);
  }
  const finalAPR =
    response?.data?.aprOverride ?? getFinanceResponseTotalAPR(response);
  if (responseData.car) {
    const name = [
      responseData.car.year,
      responseData.car.make,
      responseData.car.model,
    ]
      .filter(v => !!v)
      .join(' ');
    recap.push(['Vehicle', name, car?.name]);
  }
  if (responseData.tier) {
    recap.push(['Tier', responseData.tier, '']);
  }
  if (responseData.financeAmount) {
    recap.push([
      'Financed Amount',
      fmtPrice(responseData.financeAmount),
      fmtPrice(applicationData.financeAmount),
    ]);
  }
  if (responseData.downPayment) {
    recap.push([
      'Cash Down',
      fmtPrice(responseData.downPayment),
      fmtPrice(applicationData.downPayment),
    ]);
  }
  if (responseData.rebate) {
    recap.push([
      'Rebate',
      fmtPrice(responseData.rebate),
      fmtPrice(applicationData.rebate),
    ]);
  }
  if (responseData.monthly) {
    recap.push([
      'Monthly Payment',
      fmtPrice(responseData.monthly),
      fmtPrice(applicationData.monthly),
    ]);
  }
  if (responseData.months) {
    recap.push(['Term', responseData.months, applicationData.months]);
  }
  if (responseData.customerRate) {
    recap.push([
      'Customer Rate',
      fmtPercent(responseData.customerRate),
      fmtPercent(applicationData.apr),
    ]);
  }
  if (responseData.apr) {
    recap.push(['Buy Rate', fmtPercent(finalAPR), '']);
  }
  if (responseData.programName) {
    recap.push(['Program Name', responseData.programName, '']);
  }
  if (responseData.miscNotes) {
    recap.push(['Miscellaneous Notes', responseData.miscNotes, '']);
  }
  if (responseData.fees) {
    recap.push(['Total Lender Fees', fmtPrice(responseData.fees), '']);
  }
  if (responseData.maxPaymentToIncome) {
    recap.push([
      'Max Payment To Income',
      fmtPercent(responseData.maxPaymentToIncome),
      '',
    ]);
  }
  if (responseData.participation) {
    recap.push([
      'Dealer Participation',
      fmtPrice(responseData.participation),
      '',
    ]);
  }
  if (responseData.markup) {
    recap.push(['Mark Up %', fmtPercent(responseData.markup), '']);
  }
  if (responseData.maximumMarkup) {
    recap.push([
      'Maximum Mark Up %',
      fmtPercent(responseData.maximumMarkup),
      '',
    ]);
  }
  if (responseData.flat) {
    recap.push(['Dealer Flat', fmtPrice(responseData.flat), '']);
  }
  if (responseData.actualLTV) {
    recap.push(['LTV Actual', fmtPercent(responseData.actualLTV), '']);
  }
  if (responseData.maximumLTV) {
    recap.push(['LTV Maximum', fmtPercent(responseData.maximumLTV), '']);
  }
  return recap;
};

const getAdditionalInfo = (
  application: FinanceApplication | null,
  response: FinanceResponse | null,
  car: Car,
) => {
  const additionalInfo = [];
  const applicationData = application?.data || {
    msrp: 0,
    price: 0,
  };
  const responseData = response?.data || {};

  if (responseData.purchasePrice) {
    additionalInfo.push([
      'Purchase Price',
      fmtPrice(responseData.purchasePrice),
      '',
    ]);
  }
  if (applicationData.price) {
    additionalInfo.push(['Sales Price', '', fmtPrice(applicationData.price)]);
  }
  if (responseData.docFees) {
    additionalInfo.push(['Doc Fee', fmtPrice(responseData.docFees), '']);
  }
  if (responseData.taxAmount) {
    additionalInfo.push(['TT&L', fmtPrice(responseData.taxAmount), '']);
  }
  if (responseData.otherFees) {
    additionalInfo.push([
      'Other Fees/Misc',
      fmtPrice(responseData.otherFees),
      '',
    ]);
  }
  if (responseData.expiresAt) {
    additionalInfo.push([
      'Decision Expiration',
      moment(responseData.expiresAt).format('MM/DD/YYYY'),
      '',
    ]);
  }
  if (responseData.car?.condition) {
    additionalInfo.push([
      'New/Used',
      capitalize(responseData.car?.condition),
      capitalize(car?.condition as string),
    ]);
  }
  if (responseData.car?.id) {
    additionalInfo.push(['VIN', responseData.car?.id, car?.id]);
  }
  if (car) {
    additionalInfo.push(['Mileage', '', car.mileage || 0]);
    additionalInfo.push(['Stock', '', car.stock]);
  }
  if (applicationData.msrp) {
    additionalInfo.push(['MSRP', '', fmtPrice(applicationData.msrp)]);
  }
  return additionalInfo;
};

const renderNoOffer = () => (
  <div className="lending-offer-detail no-offer">
    <span className="no-offer-text">
      Sending this option to the salesperson will indicate that no offers are
      available.
    </span>
  </div>
);

function LendingOfferDetail({
  response,
  application,
  applicationStatus,
  deal,
  car,
  pencilResponse,
  setResponse,
  showRicTab,
  setShowRicTab,
  dealer,
  reloadResponses,
}: Props) {
  const isApproved = response?.status
    ? [FinanceResponseStatus.APPROVED, FinanceResponseStatus.BOOKED].includes(
        response?.status,
      )
    : false;
  const apr = response?.data?.apr;
  const displayMarkup = isNumber(apr) && application?.paymentDataId;
  const maximumMarkup = response?.data?.maximumMarkup ?? undefined;

  const responseOverrides = pencilResponse?.overrides;
  const markupOverride = responseOverrides?.markup;
  const hasMarkupOverride = isNumber(markupOverride);
  const markupValue = hasMarkupOverride
    ? markupOverride
    : response?.data?.markup;
  const [markup, setMarkup] = useState(markupValue);
  const [isEditingMarkup, setIsEditingMarkup] = useState(false);
  const fetchPayments = useAsyncCallback(
    PaymentsService.getPaymentsForFinanceResponse,
  );

  useEffect(() => {
    setMarkup(hasMarkupOverride ? markupOverride : response?.data?.markup || 0);
    // eslint-disable-next-line
  }, [pencilResponse]);

  if (response === null) {
    return renderNoOffer();
  }

  if (application?.provider === FinanceProvider.UPSTART) {
    return (
      <RenderUpstartOffer
        deal={deal}
        response={response}
        showRicTab={showRicTab}
        setShowRicTab={setShowRicTab}
        dealer={dealer}
        status={applicationStatus}
        reloadResponses={reloadResponses}
      />
    );
  }

  const data = response?.data || {};
  const isCondition = response?.status === FinanceResponseStatus.CONDITION;
  const isFetching = fetchPayments.loading;
  const isEditable =
    (isApproved || isCondition || isFetching) && isEditingMarkup;
  const isSelected = pencilResponse?.selected ?? false;

  return (
    <div className="lending-offer-detail">
      <div className="lending-offer-detail-header">
        <div className="title">{response?.financeSourceName} Finance Offer</div>
        <div className="markup">
          {displayMarkup && (
            <div className="lending-markup">
              <div className="pr-5">Markup</div>
              {isEditingMarkup ? (
                <PercentageInput
                  value={markup || markupValue}
                  disabled={!isEditable}
                  displayPercentSign
                  onChange={updatedMarkup => {
                    if (updatedMarkup < 0) {
                      updatedMarkup = 0;
                    }
                    setMarkup(updatedMarkup);
                  }}
                />
              ) : (
                <span>
                  {fmtPercent((markupValue as number) || (markup as number))}
                </span>
              )}
              {isNumber(maximumMarkup) && (
                <div className="pl-5">Max {fmtPercent(maximumMarkup)}</div>
              )}
              <button
                type="button"
                className="pencil-button pencil-header-button"
                onClick={() => {
                  if (isEditingMarkup) {
                    fetchPayments
                      .execute({
                        ...response,
                        data: { ...response.data, markup },
                      })
                      .then(markedUpResponse => {
                        const overriddenResponse = {
                          // @ts-ignore
                          ...markedUpResponse,
                          overrides: {
                            monthly: markedUpResponse.data.monthly,
                            markup: markedUpResponse.data.markup,
                          },
                        };
                        setResponse({
                          ...overriddenResponse,
                          selected: isSelected,
                        });
                      });
                  }

                  setIsEditingMarkup(!isEditingMarkup);
                }}
              >
                <i className="glyphicon glyphicon-pencil edit-markup" />
                {isEditingMarkup ? 'Save Markup' : 'Edit Markup'}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="lending-offer-detail-content">
        <div className="flex items-center">
          <Img
            className="finance-source-image"
            src={getImageForFinanceSource(
              response?.financeSourceName as string,
            )}
            unloader={null}
          />
          <b className="pl-15">
            Decision: {capitalize(response?.status)} by{' '}
            {response?.financeSourceName || ''}
          </b>
          &nbsp;on{' '}
          {response?.data?.receivedAt &&
            moment(response?.data?.receivedAt).format('MM/DD/YYYY - hh:mm a')}
        </div>
        <div className="row pt-10">
          <div className="col-sm-6">
            {application?.provider === FinanceProvider.ROUTEONE && (
              <>
                <div className="col-sm-4">
                  <b>RouteOne App #:</b>
                </div>
                <div className="col-sm-8">
                  {application.providerApplicationId}
                </div>
              </>
            )}
            <div className="col-sm-4">
              <b>Applicant Name:</b>
            </div>
            <div className="col-sm-8">
              {deal?.applicant?.firstName || ''}{' '}
              {deal?.applicant?.lastName || ''}
            </div>
          </div>
          <div className="col-sm-6">
            {data?.analystName && (
              <>
                <div className="col-sm-4">
                  <b>Analyst:</b>
                </div>
                <div className="col-sm-8">{data?.analystName}</div>
              </>
            )}
            {data?.analystContactInfo && (
              <>
                <div className="col-sm-4">
                  <b>Analyst Phone:</b>
                </div>
                <div className="col-sm-8">{data.analystContactInfo}</div>
              </>
            )}
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-sm-6">
            <DataTable
              rows={getDealRecap(application, pencilResponse, car)}
              headers={['Deal Recap', 'Decision', 'Application']}
              emptyMessage=""
            />
          </div>
          <div className="col-sm-6">
            <DataTable
              emptyMessage=""
              rows={getAdditionalInfo(application, pencilResponse, car)}
              headers={[
                'Additional Deal Information',
                'Decision',
                'Application',
              ]}
            />
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-sm-12">
            <h5>
              <b>Stipulations:</b>
            </h5>
            <div className="pre-line">
              {(data.stipulations || []).join('\n') || 'N/A'}
            </div>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-sm-12">
            <h5>
              <b>Comments:</b>
            </h5>
            <div className="pre-line">
              {(data.comments || []).join('\n') || 'N/A'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LendingOfferDetail;
